import { z } from "zod";
import { FocusSchema, LocaleSchema, TimestampSchema } from "./common.js";
import { ErrorCodeSchema } from "./errors.js";
import { EntityChangeSchema } from "./events.js";
import { IdSchema } from "./ids.js";
import { EffortLevelSchema, ModelPreferenceSchema } from "./model-policy.js";
import { ArtifactRefSchema, TaskStateSchema } from "./node.js";
import { ApprovalRequestSchema, ApprovalStatusSchema } from "./tool.js";
/** Bumped on breaking protocol changes; clients send it in `auth`. */
export const REALTIME_PROTOCOL_VERSION = 1;
/**
 * World stream (docs/PHASE2_PLAN.md §2.7). Additive to protocol v1: a gateway sends `world.*`
 * only to sockets that sent `world.subscribe`, so Phase 1 clients never see them.
 *
 * - Every `world.*` server message carries `seq: 0`. The per-user replay buffer never holds
 *   them; the world stream resumes by `worldSeq` (the world partition's event seq) instead.
 * - `world.subscribe` without `sinceWorldSeq`: the gateway sends a snapshot as one or more
 *   `world.snapshot` parts (`part` 0, 1, ...; the last has `final: true`; all parts carry the
 *   same `worldSeq` and `serverTime`; at most `WORLD_SNAPSHOT_MAX_NODES` nodes per part), then
 *   `world.delta`s with ops after that `worldSeq`. No delta is sent between the first and the
 *   final part.
 * - `world.subscribe` with `sinceWorldSeq: s`: when the gateway can cover every op after `s`, it
 *   sends deltas only, starting with one that covers `(s, head]` (its `ops` may be empty: an
 *   acknowledgement that carries the head). Otherwise it sends `world.reset`.
 * - `world.reset` (also possible mid-stream, e.g. after an upstream loss): the subscription has
 *   ended. The client discards its world state and cursor and sends `world.subscribe` without
 *   `sinceWorldSeq`. The gateway sends nothing more until then.
 * - `worldSeq` never decreases on one subscription. Ops are applied idempotently by node
 *   `version`, so an overlap after a resubscribe is harmless.
 * - A repeated `world.subscribe` restarts the subscription; `world.unsubscribe` ends it.
 * - `vehicle.pose` (client -> gateway): the live pose of the vehicle the user drives, at most 10 per
 *   second per vehicle. The gateway forwards it to the world service, which accepts it only from
 *   the vehicle's current driver; accepted poses go to every world subscriber as a `vehicle.pose`
 *   server message (seq 0, never replayed, not part of `worldSeq`). The entity is persisted at most
 *   every 5 seconds and when the driver exits (scene ops), so a late joiner starts from that.
 * - Node and op payloads are opaque to core; `@uzverse/world-scene` validates them
 *   (`SceneNodeSchema`, `SceneOpSchema`).
 *
 * Reserved for later, deliberately NOT in the unions yet: `world.presence.update { pose, mode,
 * insideId, floorLevel }` (client, at most 10 Hz) and `world.presence { users }` (server, by
 * interest radius). Presence is ephemeral (gateway memory), never the event log.
 */
export const WORLD_SNAPSHOT_MAX_NODES = 500;
/** Byte budget of one serialized `world.snapshot` part (below the gateway's 1 MiB send cap). */
export const WORLD_SNAPSHOT_MAX_BYTES = 256 * 1024;
/** Message types of the world stream. */
export const WORLD_MESSAGE_TYPES = [
    "world.subscribe",
    "world.unsubscribe",
    "world.snapshot",
    "world.delta",
    "world.reset",
    "vehicle.pose",
];
const OpaqueRecordSchema = z.record(z.string(), z.unknown());
/** Messages the browser sends to the gateway. */
export const ClientMessageSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("auth"),
        token: z.string().min(1),
        protocol: z.literal(REALTIME_PROTOCOL_VERSION),
        /** Resume after reconnect: the last server `seq` the client processed. */
        lastSeq: z.number().int().nonnegative().optional(),
    }),
    z.object({
        type: z.literal("chat.send"),
        clientMsgId: z.string().min(1),
        text: z.string().min(1),
        focus: FocusSchema.nullable(),
        locale: LocaleSchema.optional(),
        /** Per-message model/effort override (additive). */
        modelPreference: ModelPreferenceSchema.optional(),
    }),
    z.object({ type: z.literal("task.subscribe"), taskId: IdSchema }),
    z.object({ type: z.literal("task.unsubscribe"), taskId: IdSchema }),
    z.object({
        type: z.literal("approval.response"),
        approvalId: IdSchema,
        decision: z.enum(["approved", "rejected"]),
        note: z.string().optional(),
    }),
    z.object({ type: z.literal("ping"), nonce: z.string().optional() }),
    z.object({
        type: z.literal("world.subscribe"),
        /** Resume: the last `worldSeq` the client applied. Omit for a fresh snapshot. */
        sinceWorldSeq: z.number().int().nonnegative().optional(),
    }),
    z.object({ type: z.literal("world.unsubscribe") }),
    /**
     * Live pose of the vehicle the user is driving (at most 10 per second per vehicle; extra ones
     * are dropped). Only the driver's are relayed. Never a tool call.
     */
    z.object({
        type: z.literal("vehicle.pose"),
        vehicleId: IdSchema,
        x: z.number().finite(),
        z: z.number().finite(),
        yawDeg: z.number().finite(),
        speed: z.number().finite(),
        /** Optional (additive): front wheel steer angle (rad, + = left) and the lamps bitmask (1 head, 2 brake, 4 reverse, 8 left, 16 right indicator). */
        steer: z.number().finite().min(-1.5).max(1.5).optional(),
        lights: z.number().int().min(0).max(255).optional(),
    }),
]);
/** Server message bodies (before the gateway adds `seq`). */
export const ServerMessageBodySchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("auth.ok"), userId: IdSchema, resumedFromSeq: z.number().int() }),
    z.object({ type: z.literal("chat.accepted"), clientMsgId: z.string(), taskId: IdSchema }),
    z.object({
        type: z.literal("chat.delta"),
        taskId: IdSchema,
        messageId: z.string(),
        delta: z.string(),
    }),
    z.object({
        type: z.literal("chat.message"),
        taskId: IdSchema,
        messageId: z.string(),
        text: z.string(),
    }),
    z.object({
        type: z.literal("task.status"),
        taskId: IdSchema,
        state: TaskStateSchema,
        detail: z.string().optional(),
    }),
    z.object({ type: z.literal("task.artifact"), taskId: IdSchema, artifact: ArtifactRefSchema }),
    z.object({
        type: z.literal("task.model"),
        taskId: IdSchema,
        model: z.string().min(1),
        effort: EffortLevelSchema.optional(),
        source: z.string().optional(),
    }),
    z.object({ type: z.literal("entity.changed"), change: EntityChangeSchema }),
    z.object({ type: z.literal("approval.request"), approval: ApprovalRequestSchema }),
    z.object({
        type: z.literal("approval.resolved"),
        approvalId: IdSchema,
        status: ApprovalStatusSchema,
    }),
    z.object({ type: z.literal("pong"), nonce: z.string().optional() }),
    z.object({ type: z.literal("error"), code: ErrorCodeSchema, message: z.string() }),
    z.object({
        type: z.literal("world.snapshot"),
        worldSeq: z.number().int().nonnegative(),
        serverTime: TimestampSchema,
        part: z.number().int().nonnegative(),
        final: z.boolean(),
        /** Scene nodes (opaque to core). */
        nodes: z.array(OpaqueRecordSchema).max(WORLD_SNAPSHOT_MAX_NODES),
    }),
    z.object({
        type: z.literal("world.delta"),
        /** The world seq this delta brings the client to. */
        worldSeq: z.number().int().nonnegative(),
        /** Scene ops (opaque to core), in order. May be empty (a cursor acknowledgement). */
        ops: z.array(OpaqueRecordSchema),
    }),
    z.object({ type: z.literal("world.reset") }),
    /** A driven vehicle's live pose, relayed to every world subscriber (seq 0, never replayed). */
    z.object({
        type: z.literal("vehicle.pose"),
        vehicleId: IdSchema,
        x: z.number().finite(),
        z: z.number().finite(),
        yawDeg: z.number().finite(),
        speed: z.number().finite(),
        /** Optional (additive): front wheel steer angle (rad, + = left) and the lamps bitmask (1 head, 2 brake, 4 reverse, 8 left, 16 right indicator). */
        steer: z.number().finite().min(-1.5).max(1.5).optional(),
        lights: z.number().int().min(0).max(255).optional(),
    }),
]);
/** True for the `world.*` messages, which are never sequenced or replayed. */
export function isWorldMessage(message) {
    return WORLD_MESSAGE_TYPES.includes(message.type);
}
/**
 * Every server message carries a per-session `seq` (strictly increasing, starting at 1) so a
 * reconnecting client resumes from `lastSeq` without loss or duplication. `pong`, `error` and
 * every `world.*` message use seq 0 and are not replayed (world messages must use 0).
 */
export const ServerMessageSchema = z
    .intersection(z.object({ seq: z.number().int().nonnegative() }), ServerMessageBodySchema)
    .refine((m) => !isWorldMessage(m) || m.seq === 0, {
    message: "world.* messages carry seq 0",
    path: ["seq"],
});
export function parseClientMessage(raw) {
    return ClientMessageSchema.parse(raw);
}
export function parseServerMessage(raw) {
    return ServerMessageSchema.parse(raw);
}
//# sourceMappingURL=realtime.js.map